import { getFirestore, collection, addDoc, getDocs, getDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { auth, JOB_DOC_NAME } from '../firebase/firebaseConfig';

// Initialize Firestore
const db = getFirestore();

// Job post interface
export interface JobPost {
  id?: string;
  title: string;
  description: string;
  experience: string;
  vacancy: string;
  location: string;
  imageUrl: string;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
}


// Create a new job post
export const createJobPost = async (jobData: FormData): Promise<{ success: boolean, id?: string, error?: string }> => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return { success: false, error: 'User not authenticated' };
    }
    
    // Extract data from FormData
    const title = jobData.get('title') as string;
    const description = jobData.get('description') as string;
    const experience = jobData.get('experience') as string;
    const vacancy = jobData.get('vacancy') as string;
    const location = jobData.get('location') as string;
    const imageUrl = jobData.get('imageUrl') as string;
    
    // Create job post document
    const jobPost: Omit<JobPost, 'id'> = {
      title,
      description,
      experience,
      vacancy,
      imageUrl,
      location,
      createdAt: serverTimestamp(),
      createdBy: currentUser.uid,
      updatedAt: serverTimestamp()
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, JOB_DOC_NAME), jobPost);
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating job post:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Get all job posts
export const getJobPosts = async (): Promise<JobPost[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, JOB_DOC_NAME));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as JobPost));
  } catch (error) {
    console.error('Error fetching job posts:', error);
    return [];
  }
};

// Get a single job post
export const getJobPost = async (id: string): Promise<JobPost | null> => {
  try {
    const docRef = doc(db, JOB_DOC_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as JobPost;
  } catch (error) {
    console.error('Error fetching job post:', error);
    return null;
  }
};

// Alias for getJobPost to maintain compatibility
export const getJobPostById = getJobPost;

// Update a job post
export const updateJobPost = async (jobData: JobPost): Promise<boolean> => {
  try {
    if (!jobData.id) {
      return false;
    }
    
    const docRef = doc(db, JOB_DOC_NAME, jobData.id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return false;
    }
    
    // Update the document with new data
    await updateDoc(docRef, {
      title: jobData.title,
      description: jobData.description,
      experience: jobData.experience,
      vacancy: jobData.vacancy,
      imageUrl: jobData.imageUrl,
      location: jobData.location,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating job post:', error);
    return false;
  }
};

// Delete a job post
export const deleteJobPost = async (id: string): Promise<boolean> => {
  try {
    const docRef = doc(db, JOB_DOC_NAME, id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting job post:', error);
    return false;
  }
}; 